import * as React from 'react'
import { useEffect, useId, useMemo, useState } from 'react'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import MuiSelect, { SelectChangeEvent } from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import { FormattedMessage } from 'react-intl'
import styles from './Select.module.css'

interface SelectProps {
  options: string[]
  onChange?: (value: string) => void
  label?: string
  defaultValue?: string
  size?: 'small' | 'large'
  transparent?: boolean
}

const Select: React.FC<SelectProps> = ({
  options,
  onChange,
  label,
  defaultValue,
  size = 'large',
  transparent = false,
}) => {
  const reactId = useId()
  const labelId = `${reactId}-label`
  const selectId = `${reactId}-select`

  const initialValue = useMemo(
    () => defaultValue ?? options[0] ?? '',
    [defaultValue, options]
  )

  const [selected, setSelected] = useState<string>(initialValue)

  useEffect(() => {
    if (defaultValue) setSelected(defaultValue)
  }, [defaultValue])

  const formSize = size === 'small' ? 'small' : 'medium'

  const fcClasses = [
    styles.formControl,
    size === 'small' ? styles.formControlSmall : '',
    transparent ? styles.transparent : '',
  ]
    .filter(Boolean)
    .join(' ')

  const handleChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value
    setSelected(value)
    onChange?.(value)
  }

  return (
    <FormControl size={formSize} className={fcClasses}>
      {label ? (
        <InputLabel id={labelId} className={styles.label}>
          {label}
        </InputLabel>
      ) : null}

      <MuiSelect<string>
        id={selectId}
        labelId={label ? labelId : undefined}
        label={label ? label : undefined}
        value={selected}
        onChange={handleChange}
        displayEmpty
        className={styles.select}
        inputProps={{
          'aria-label': label ?? 'Select',
        }}
        MenuProps={{
          PaperProps: {
            className: transparent
              ? styles.transparentMenuPaper
              : styles.menuPaper,
          },
          MenuListProps: {
            className: transparent
              ? styles.transparentMenuList
              : styles.menuList,
          },
        }}
      >
        {options.map((opt) => (
          <MenuItem
            key={opt}
            value={opt}
            className={[
              styles.menuItem,
              transparent ? styles.transparentMenuItem : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span className={styles.selectOption}>
              <FormattedMessage id={opt} />
            </span>
          </MenuItem>
        ))}
      </MuiSelect>
    </FormControl>
  )
}

export default Select
